import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Loader2 } from "lucide-react";

import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Trade } from "../../../../types/trade";
import TradeDetailsCard, {
  SHOW_TRADE_DETAILS_MOOD_SELECTS,
  type TradeDetailsJournalActions,
} from "./TradeDetailsCard";
import { TradeDetailsInlineTradeReview } from "./TradeDetailsInlineTradeReview";

export type TradeDetailsInlinePanelHandle = {
  isDirty: () => boolean;
  requestNavigate: (action: () => void) => void;
};

interface TradeDetailsInlinePanelProps {
  trade: Trade;
  onJournalSaved?: (trade: Trade) => void;
  /** Kept for call-site compatibility; stats already live on the main table row. */
  brokerOpenTradeIdSet?: ReadonlySet<string>;
}

const TradeDetailsInlinePanel = forwardRef<
  TradeDetailsInlinePanelHandle,
  TradeDetailsInlinePanelProps
>(function TradeDetailsInlinePanel(
  { trade, onJournalSaved },
  ref
) {
  const journalActionsRef = useRef<TradeDetailsJournalActions | null>(null);
  const pendingActionRef = useRef<(() => void) | null>(null);

  const [isJournalDirty, setIsJournalDirty] = useState(false);
  const [isSavingJournal, setIsSavingJournal] = useState(false);
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      isDirty: () => isJournalDirty,
      requestNavigate: (action) => {
        if (isJournalDirty) {
          pendingActionRef.current = action;
          setUnsavedDialogOpen(true);
          return;
        }
        action();
      },
    }),
    [isJournalDirty]
  );

  const registerJournalActions = useCallback(
    (actions: TradeDetailsJournalActions | null) => {
      journalActionsRef.current = actions;
    },
    []
  );

  const runPendingAction = useCallback(() => {
    const pendingAction = pendingActionRef.current;
    pendingActionRef.current = null;
    pendingAction?.();
  }, []);

  const handleSaveChanges = useCallback(async () => {
    if (!journalActionsRef.current) return false;
    setIsSavingJournal(true);
    try {
      return await journalActionsRef.current.save();
    } finally {
      setIsSavingJournal(false);
    }
  }, []);

  const handleExitWithoutSaving = () => {
    journalActionsRef.current?.discard();
    setUnsavedDialogOpen(false);
    runPendingAction();
  };

  const handleSaveFromUnsavedDialog = async () => {
    const saved = await handleSaveChanges();
    if (!saved) return;
    setUnsavedDialogOpen(false);
    runPendingAction();
  };

  return (
    <>
      <div
        data-trade-details-expanded-panel=""
        data-no-row-click=""
        className={cn(
          "min-w-0 max-w-full overflow-visible border-t border-border bg-muted/15 dark:bg-sidebar/40",
          "animate-in fade-in-0 slide-in-from-top-1 duration-200"
        )}
      >
        {/*
          Expanded row layout (hard cut — no trade-stats table here):
          1) Journal: market screenshot + notes (row already shows P&L / fields)
          2) Single-trade Trade Review chart + legend
        */}
        <div
          data-trade-details-expanded-stack=""
          className="flex w-full min-w-0 flex-col gap-6"
        >
          <TradeDetailsCard
            trade={trade}
            hideHeader
            journalOnly
            onDirtyChange={setIsJournalDirty}
            onRegisterJournalActions={registerJournalActions}
            onJournalSaved={onJournalSaved}
          />

          <TradeDetailsInlineTradeReview trade={trade} />
        </div>

        {isJournalDirty ? (
          <div
            data-trade-details-inline-footer=""
            className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-border pt-3"
          >
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              className="shrink-0"
              onClick={handleSaveChanges}
              disabled={isSavingJournal}
            >
              {isSavingJournal ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  <span>Saving...</span>
                </>
              ) : (
                <span>Save changes</span>
              )}
            </Button>
          </div>
        ) : null}
      </div>

      <StrategyPineDetailDialog
        open={unsavedDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            pendingActionRef.current = null;
            setUnsavedDialogOpen(false);
          }
        }}
        title="Unsaved changes"
        description="You have unsaved changes to this trade journal. Save your changes or exit without saving."
        size="compact"
        showDismissButton={false}
        contentClassName="sm:max-w-md"
        bodyClassName="flex-none overflow-visible"
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-font="primary"
              onClick={handleExitWithoutSaving}
            >
              Exit without saving
            </Button>
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              className="shrink-0"
              onClick={handleSaveFromUnsavedDialog}
              disabled={isSavingJournal}
            >
              {isSavingJournal ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  <span>Saving...</span>
                </>
              ) : (
                <span>Save changes</span>
              )}
            </Button>
          </div>
        }
      >
        <p className="font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-sidebar-muted-foreground">
          {SHOW_TRADE_DETAILS_MOOD_SELECTS
            ? "Mood selections, notes, and screenshot changes will be lost if you exit without saving."
            : "Notes and screenshot changes will be lost if you exit without saving."}
        </p>
      </StrategyPineDetailDialog>
    </>
  );
});

export default TradeDetailsInlinePanel;
